import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Input,
    Button,
    InputGroup,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Alert,
    Badge
} from 'reactstrap';

const AirSearchTracking = ({ searchFormik }) => {
    const [containerNum, setContainerNum] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [autoDetect, setAutoDetect] = useState('Auto Detect');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [error, setError] = useState('');
    const [inputError, setInputError] = useState('');
    const [isSearchActive, setIsSearchActive] = useState(false);

    // Get parent loading state from Tracking.jsx
    const { trackingData } = useSelector((state) => state?.globalReducer);

    
    // Component-specific styles
    const containerSearchStyles = {
        containerWrapper: {
            position: 'relative'
        },
        card: {
            transition: 'all 0.2s ease',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        },
        cardHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        },
        button: {
            transition: 'all 0.2s ease',
            fontWeight: '500'
        },
        buttonHover: {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 20px rgba(0,123,255,0.4)'
        },
        pulse: {
            animation: 'pulse 2s infinite'
        },
        
    };

    // Custom CSS for animations that can't be done with inline styles
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = `
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          /* Container shift animation */
          @keyframes shiftToTop {
            0% { 
              transform: translateY(0);
              max-width: 100%;
            }
            100% { 
              transform: translateY(-150px);
              max-width: 800px;
            }
          }
          
          /* Search container animations */
          .search-container {
            transition: all 0.5s ease;
            border-radius: 16px;
            padding: 20px;
            background-color: white;
            margin: 0 auto;
            position: relative;
            z-index: 10;
          }
          
          .search-container.active {
            animation: shiftToTop 0.5s forwards;
          }
          
          .features-container {
            transition: opacity 0.5s ease;
          }
          
          .features-container.hidden {
            opacity: 0;
            pointer-events: none;
          }
          
          
          
        `;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const handleSearch = async () => {
        if (!searchFormik?.values?.trackingNumber?.trim()) {
            setInputError('Please enter a container number');
            return;
        }

        setError('');
        setInputError('');
        setIsLoading(true);
        setIsSearchActive(true); // Trigger the animation

        // Submit the form using formik
        searchFormik.handleSubmit();
    };

    useEffect(() => {
        if (searchFormik?.errors?.trackingNumber) {
            setError(searchFormik?.errors?.trackingNumber);
            setIsLoading(false);
        } else {
            setError('');
            setInputError('');
        }
    }, [searchFormik.errors.trackingNumber]);

    // Sync loading state with parent component
    useEffect(() => {
        // When tracking data changes and we have a response, reset loading state
        if (trackingData && isLoading) {
            // The parent component will set isLoading to false after a delay
            // We just need to make sure our component knows when the search is complete
            if (trackingData?.error?.code === 0 || trackingData?.error) {
                // Give a slight delay before resetting loading state to match parent component
                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            }
        }
    }, [trackingData]);

    const handleInputChange = (e) => {
        setContainerNum(e.target.value);
        if (inputError) setInputError('');
        if (error) setError('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleDetectionChange = (option) => {
        setAutoDetect(option);
        setIsDropdownOpen(false);
    };

    const customStyles = {
        pageBackground: {
            minHeight: '100vh',
            //background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
            paddingTop: '2rem',
            paddingBottom: '2rem'
        },
        headerIcon: {
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
        },
        titleGradient: {
            background: 'linear-gradient(135deg, #212529 0%, #007bff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            margin: 0
        },
        searchCard: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1
        },
        searchButton: {
            background: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
        },
        dropdownToggle: {
            borderRadius: '0 12px 12px 0',
            height: '48px',
            backgroundColor: '#f8f9fa',
            borderColor: '#dee2e6',
            color: '#495057',
            borderLeft: 'none',
            minWidth: '160px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '12px',
            paddingRight: '12px',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s ease'
        },
        dropdownMenu: {
            position: 'absolute',
            zIndex: 1050,
            backgroundColor: 'rgb(255, 255, 255)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            boxShadow: 'rgba(0, 0, 0, 0.15) 0px 8px 25px',
            minWidth: '200px',
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '4px',
            padding: '8px 0px'
        },
        dropdownItem: {
            padding: '10px 16px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '0.9rem',
            color: '#495057',
            display: 'flex',
            alignItems: 'center',
            transition: 'all 0.15s ease-in-out'
        },
        featureCard: {
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '12px',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
        },
        featureIcon: {
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
    };

    return (
        <div style={customStyles.pageBackground} className="container-search-component">
            <Container className="py-4">
                <Row className="justify-content-center">
                    <Col lg={10} xl={8}>
                        {/* Header */}
                        <div className="text-center mb-4">
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                <div style={customStyles.headerIcon} className="me-3">
                                    <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                </div>
                                <h1 style={customStyles.titleGradient}>Air Cargo Tracker</h1>
                            </div>
                            <p className="text-muted">Track your air shipments in real-time</p>
                        </div>

                        {/* Search Card */}
                        <div className={`search-container ${isSearchActive ? 'active' : ''}`}>
                            <Card
                                style={{ ...customStyles.searchCard, ...containerSearchStyles.card, border: 'none' }}
                                className="mb-0"
                                onMouseEnter={(e) => {
                                    if (!isSearchActive) {
                                        e.currentTarget.style.transform = containerSearchStyles.cardHover.transform;
                                        e.currentTarget.style.boxShadow = containerSearchStyles.cardHover.boxShadow;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSearchActive) {
                                        e.currentTarget.style.transform = '';
                                        e.currentTarget.style.boxShadow = customStyles.searchCard.boxShadow;
                                    }
                                }}
                            >
                                <CardBody className="p-4">
                                    <Row className="align-items-end">
                                        <Col lg={8} className="mb-3 mb-lg-0">
                                            <InputGroup style={{ position: 'relative' }}>
                                                <Input
                                                    name="trackingNumber"
                                                    type="text"
                                                    value={searchFormik.values.trackingNumber || ''}
                                                    onChange={searchFormik.handleChange}
                                                    onKeyPress={handleKeyPress}
                                                    placeholder="Enter container number (e.g., ABCU1234567)"
                                                    invalid={!!inputError}
                                                    style={{ borderRadius: '12px 12px 12px 12px', height: '48px' }}
                                                />
                                            </InputGroup>
                                            {inputError && (
                                                <div className="text-danger mt-2 d-flex align-items-center">
                                                    <small>
                                                        <i className="fas fa-exclamation-circle me-2"></i>
                                                        {inputError}
                                                    </small>
                                                </div>
                                            )}
                                        </Col>
                                        <Col lg={4}>
                                            <Button
                                                style={{ ...customStyles.searchButton, ...containerSearchStyles.button }}
                                                block
                                                disabled={!searchFormik?.values?.trackingNumber?.trim() || isLoading}
                                                onClick={handleSearch}
                                                className="w-100"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = containerSearchStyles.buttonHover.transform;
                                                    e.currentTarget.style.boxShadow = containerSearchStyles.buttonHover.boxShadow;
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = '';
                                                    e.currentTarget.style.boxShadow = customStyles.searchButton.boxShadow;
                                                }}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"
                                                            style={containerSearchStyles.pulse}></span>
                                                        Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-search me-2"></i>
                                                        Find Air Container
                                                    </>
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>

                                    {error && (
                                        <Alert color="danger" className="mt-3 d-flex align-items-start">
                                            <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                                            <div className="flex-grow-1">
                                                <strong>Search Error</strong>
                                                <div>{error}</div>
                                            </div>
                                            <Button
                                                close
                                                onClick={() => setError('')}
                                                aria-label="Close"
                                            />
                                        </Alert>
                                    )}
                                </CardBody>
                            </Card>
                        </div>

                        {/* Features */}
                        <Row className={`mb-4 features-container ${isSearchActive ? 'hidden' : ''}`}>
                            <Col md={4} className="mb-3">
                                <Card
                                    style={customStyles.featureCard}
                                    className="h-100 p-3 container-search-card"
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)'}
                                >
                                    <CardBody className="p-0">
                                        <div className="d-flex align-items-center">
                                            <div style={{ ...customStyles.featureIcon, backgroundColor: '#e3f2fd' }} className="me-3">
                                                <i className="fas fa-clock text-primary"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-1 fw-bold">Real-time</h6>
                                                <small className="text-muted">Live updates</small>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4} className="mb-3">
                                <Card
                                    style={customStyles.featureCard}
                                    className="h-100 p-3 container-search-card"
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)'}
                                >
                                    <CardBody className="p-0">
                                        <div className="d-flex align-items-center">
                                            <div style={{ ...customStyles.featureIcon, backgroundColor: '#e8f5e8' }} className="me-3">
                                                <i className="fas fa-shield-alt text-success"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-1 fw-bold">Secure</h6>
                                                <small className="text-muted">Protected data</small>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4} className="mb-3">
                                <Card
                                    style={customStyles.featureCard}
                                    className="h-100 p-3 container-search-card"
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)'}
                                >
                                    <CardBody className="p-0">
                                        <div className="d-flex align-items-center">
                                            <div style={{ ...customStyles.featureIcon, backgroundColor: '#f3e5f5' }} className="me-3">
                                                <i className="fas fa-bolt text-warning"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-1 fw-bold">Fast</h6>
                                                <small className="text-muted">Instant results</small>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AirSearchTracking;
